'use client';

import { createStore } from 'zustand';

import { EnvironmentNameEnum, getEnvironmentNameFromUrl } from '@/config/environment';
import { AuthenticatedUser } from '@/types/auth-base.types';
import { LoadedAuthenticatedUser, tryToLoadAuthenticatedUser } from '@/services/auth/user.service';
import { getAuthTokens, removeAuthTokens } from '@/storage/auth-tokens.storage';
import { setAuthSessionHint, removeAuthSessionHint } from '@/storage/auth-session-hint.storage';
import { resetCart } from '@/store/cart/cart.store';
import { resetAppointmentsLists } from '@/store/appointments/appointments-list.store';
import { closePatientSelection } from '@/store/patient-selection/patient-selection.store';
import { closeScheduleSelection } from '@/store/schedule-selection/schedule-selection.store';
import { clearLogIdentity, setLogIdentity } from '@/logger/identity.client';
import { identifyAnalyticsUser, resetAnalyticsIdentity } from '@/utils/analytics/identity.util';
import { HttpException } from '@/utils/api/api-client';
import { getJwtExpiresAt } from '@/utils/auth/jwt.util';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export type AuthStoreState = {
  authenticatedUser: AuthenticatedUser | undefined;
  authStatus: AuthStatus;
  isBasicProfile: boolean;
  phoneConfirmedAt: string | undefined;
  sessionExpired: boolean;
};

export type ComputedAuthStoreState = {
  computed: {
    isLoggedIn: boolean;
    isAuthReady: boolean;
  };
};

const initialAuthStatus: AuthStatus
  = typeof window !== 'undefined' && getAuthTokens()?.access_token ? 'loading' : 'unauthenticated';

export const authStore = createStore<AuthStoreState & ComputedAuthStoreState>((set, get) => {
  return {
    authenticatedUser: undefined,
    authStatus: initialAuthStatus,
    isBasicProfile: false,
    phoneConfirmedAt: undefined,
    sessionExpired: false,
    computed: {
      get isLoggedIn() {
        return !!get().authenticatedUser;
      },
      get isAuthReady() {
        return get().authStatus !== 'loading';
      },
    },
  };
});

export const setAuthenticatedUser = (user: AuthenticatedUser | undefined, isBasicProfile = false) => {
  const { authenticatedUser: previousUser } = authStore.getState();
  const isSameCustomer = Boolean(user?.id) && user?.id === previousUser?.id;

  authStore.setState({
    authenticatedUser: user,
    authStatus: user ? 'authenticated' : 'unauthenticated',
    isBasicProfile: Boolean(user) && isBasicProfile,
    ...(user ? { sessionExpired: false } : {}),
    ...(isSameCustomer ? {} : { phoneConfirmedAt: undefined }),
  });
  setAuthSessionHint(!!user);
  setLogIdentity(user?.id);
  identifyAnalyticsUser(user?.id);
};

if (typeof window !== 'undefined' && getEnvironmentNameFromUrl() === EnvironmentNameEnum.LOCAL) {
  (window as unknown as { __medprevAuthStore?: typeof authStore }).__medprevAuthStore = authStore;
}

export const confirmCustomerPhone = (phone: string) => {
  const { authenticatedUser } = authStore.getState();

  authStore.setState({
    phoneConfirmedAt: new Date().toISOString(),
    ...(authenticatedUser
      ? {
          authenticatedUser: {
            ...authenticatedUser,
            contact: {
              email: authenticatedUser.contact?.email ?? '',
              phones: [phone, ...(authenticatedUser.contact?.phones ?? []).slice(1)],
            },
          },
        }
      : {}),
  });
};

export const clearPhoneConfirmation = () => {
  authStore.setState({ phoneConfirmedAt: undefined });
};

export async function logoutUser() {
  authStore.setState({
    authenticatedUser: undefined,
    authStatus: 'unauthenticated',
    isBasicProfile: false,
    phoneConfirmedAt: undefined,
    sessionExpired: false,
  });
  removeAuthTokens();
  removeAuthSessionHint();
  clearLogIdentity();
  resetAnalyticsIdentity();
  resetCart();
  resetAppointmentsLists();
  closePatientSelection();
  closeScheduleSelection();
}

function markSessionUnresolved() {
  authStore.setState({
    authenticatedUser: undefined,
    authStatus: 'unauthenticated',
    isBasicProfile: false,
    phoneConfirmedAt: undefined,
  });
  setAuthSessionHint(false);
  clearLogIdentity();
  resetCart();
  resetAppointmentsLists();
  closePatientSelection();
  closeScheduleSelection();
}

function isAuthenticationFailure(error: unknown): boolean {
  return error instanceof HttpException && (error.status === 401 || error.status === 403);
}

export function expireSession() {
  const { authenticatedUser, sessionExpired } = authStore.getState();

  if (sessionExpired || (!authenticatedUser && !getAuthTokens()?.access_token)) {
    return;
  }

  removeAuthTokens();
  markSessionUnresolved();
  authStore.setState({ sessionExpired: true });
}

export function expireSessionIfTokenExpired(): boolean {
  const expiresAt = getJwtExpiresAt(getAuthTokens()?.access_token);

  if (expiresAt === null || expiresAt > Date.now()) {
    return false;
  }

  expireSession();
  return true;
}

export async function resolveAuthenticatedUser(accessToken?: string) {
  const token = accessToken || getAuthTokens()?.access_token;
  if (!token) {
    markSessionUnresolved();
    return;
  }

  let loaded: LoadedAuthenticatedUser | undefined;

  try {
    loaded = await tryToLoadAuthenticatedUser(token);
  } catch (error) {
    if (isAuthenticationFailure(error)) {
      expireSession();
      return;
    }

    throw error;
  }

  if (!loaded) {
    markSessionUnresolved();
    return;
  }
  setAuthenticatedUser(loaded.user, loaded.isBasicProfile);
}
